import { useEffect, useRef, useState } from 'react';
import { Camera as CameraIcon, Loader2, X } from 'lucide-react';

// Live camera capture for browsers (desktop included). Native apps use the
// Capacitor camera instead; this covers every other device with a camera.
// Callers should check `typeof navigator.mediaDevices?.getUserMedia ===
// 'function'` and fall back to a file input when it's unavailable.
export default function WebcamCaptureModal({
  onCapture,
  onCancel,
  onPickFileInstead,
  facingMode = 'user',
  title = 'Take a photo',
}: {
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
  onPickFileInstead: () => void;
  // 'user' = front/selfie camera (mirrored preview); 'environment' = rear.
  facingMode?: 'user' | 'environment';
  title?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [camError, setCamError] = useState('');

  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => {
        if (!cancelled) {
          setCamError(
            'Could not access your camera. Allow camera access in your browser, or choose a file instead.',
          );
        }
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [facingMode]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) onCapture(blob);
    }, 'image/png');
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-navy">{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close camera"
            className="p-1 text-gray-400 hover:text-navy transition"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {camError ? (
            <p className="text-sm text-gray-600">{camError}</p>
          ) : (
            <div className="relative rounded-xl overflow-hidden bg-gray-900 aspect-square">
              {/* Selfie preview is mirrored like a mirror so framing feels
                  natural; the saved photo comes from the unmirrored stream. */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                onLoadedMetadata={() => setReady(true)}
                className="h-full w-full object-cover"
                style={facingMode === 'user' ? { transform: 'scaleX(-1)' } : undefined}
              />
              {!ready && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 size={26} className="animate-spin text-white/80" />
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onPickFileInstead}
              className="px-3 py-2 text-sm text-gray-600 hover:text-navy transition"
            >
              Choose a file instead
            </button>
            {!camError && (
              <button
                type="button"
                onClick={capture}
                disabled={!ready}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-light transition disabled:opacity-50"
              >
                <CameraIcon size={15} />
                Capture
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
